import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { getColorFromPercentage } from '../utils/colorUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const RequirementPointsChart = ({ categories }) => {
  const maxScore = 5.0;

  // Count requirements by their exact integer score (1, 2, 3, 4, 5)
  const scoreDistribution = useMemo(() => {
    // Initialize counts for scores 1, 2, 3, 4, 5
    const counts = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    };

    categories.forEach(category => {
      if (category.details && category.details.length > 0) {
        category.details.forEach(detail => {
          const score = detail.score === 'N/A' || detail.score === null || detail.score === undefined
            ? null
            : (typeof detail.score === 'number' ? detail.score : parseFloat(detail.score) || null);

          if (score !== null) {
            // Round to nearest integer
            const roundedScore = Math.round(score);
            
            // Only count scores between 1 and 5
            if (roundedScore >= 1 && roundedScore <= 5) {
              counts[roundedScore]++;
            }
          }
        });
      }
    });

    return counts;
  }, [categories]);

  // Check if there's any data
  const totalRequirements = Object.values(scoreDistribution).reduce((sum, count) => sum + count, 0);
  
  if (totalRequirements === 0) {
    return null;
  }

  const labels = Object.keys(scoreDistribution).map(key => `${key}`);
  const data = Object.values(scoreDistribution);

  // Calculate colors based on score (higher score = better color)
  const getScoreColor = (score) => {
    const percentage = (score / maxScore) * 100;
    return getColorFromPercentage(percentage);
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Number of Requirements',
        data: data,
        backgroundColor: Object.keys(scoreDistribution).map(score => 
          getScoreColor(parseInt(score)).replace('rgb', 'rgba').replace(')', ', 0.8)')
        ),
        borderColor: Object.keys(scoreDistribution).map(score => getScoreColor(parseInt(score))),
        borderWidth: 2,
        borderRadius: 6
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#121b29',
        bodyColor: '#27303e',
        borderColor: '#dce2ec',
        borderWidth: 1,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            const count = context.parsed.y;
            const percentage = ((count / totalRequirements) * 100).toFixed(1);
            return `${count} requirements (${percentage}%)`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#6b788d',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        title: {
          display: true,
          text: 'Number of Requirements',
          color: '#27303e',
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          padding: {
            top: 0,
            bottom: 15
          }
        }
      },
      x: {
        ticks: {
          color: '#6b788d',
          font: {
            size: 12
          }
        },
        grid: {
          display: false
        },
        title: {
          display: true,
          text: 'Points',
          color: '#27303e',
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          padding: {
            top: 15,
            bottom: 0
          }
        }
      }
    }
  };

  return (
    <div className="p-6 m-0 bg-transparent border-0 shadow-none text-center">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">Requirement Score Distribution</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 m-0">Number of requirements by their score</p>
      <div className="w-full h-[400px] flex items-center justify-center transition-transform duration-200 mb-4 min-h-[400px] overflow-visible hover:scale-[1.01]">
        <Bar data={chartData} options={chartOptions} />
      </div>
      <div className="flex gap-6 pt-4 border-t border-gray-200 dark:border-gray-600 flex-wrap">
        <div className="flex gap-2 items-center">
          <span className="text-sm text-gray-500 dark:text-gray-400">Total Requirements:</span>
          <span className="text-base font-semibold text-gray-900 dark:text-white">{totalRequirements}</span>
        </div>
      </div>
    </div>
  );
};

export default RequirementPointsChart;
